import { Inject, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import * as DashboardActions from './device-dashboard.actions';
import * as AppActions from '../../store/app/actions';

@Injectable()
export class DeviceDashboardEffects {
  constructor(
    private actions$: Actions<DashboardActions.DeviceDashboardActions>,
    private http: HttpClient,
    private store: Store<any>,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
  ) {}

  @Effect()
  loadLists$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.LoadLists),
    switchMap(() => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.get<any>(`${this.baseUrl}/dashboard-list`).pipe(
        switchMap((res) => of(new DashboardActions.LoadListsSuccess(res.data), new AppActions.HideLoader())),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  createList$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.CreateList),
    switchMap((action: DashboardActions.CreateList) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.post<any>(`${this.baseUrl}/dashboard-list`, action.payload).pipe(
        switchMap((res) => [
          new DashboardActions.CreateListSuccess(res.data),
          new DashboardActions.LoadLists(), // Refresh list dropdown
          new AppActions.HideLoader(),
        ]),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  loadDevices$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.LoadDevices),
    switchMap((action: DashboardActions.LoadDevices) => {
      if (!action.listId) {
        return of(new DashboardActions.LoadDevicesSuccess([]));
      }
      this.store.dispatch(new AppActions.ShowLoader());
      const params = new HttpParams().set('dashboardListId', action.listId.toString());
      return this.http.get<any>(`${this.baseUrl}/dashboard-list-device`, { params }).pipe(
        switchMap((res) => of(new DashboardActions.LoadDevicesSuccess(res.data), new AppActions.HideLoader())),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  updateDevice$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.UpdateDevice),
    switchMap((action: DashboardActions.UpdateDevice) => {
      return this.http.patch<any>(`${this.baseUrl}/dashboard-list-device/${action.id}`, action.payload).pipe(
        switchMap((response) => of(new DashboardActions.UpdateDeviceSuccess(response))),
        catchError((err) => of(new DashboardActions.FetchError(err))),
      );
    }),
  );

  @Effect()
  updateBulkDevices$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.UpdateBulkDevices),
    switchMap((action: DashboardActions.UpdateBulkDevices) => {
      this.store.dispatch(new AppActions.ShowLoader());

      const formattedDevices = action.payload.map((hex) => ({
        id: hex.id,
        dashboardListId: action.listId,
        position: hex.position,
        deviceId: hex.deviceId,
        deviceName: hex.name,
        alertStatus: hex.alertStatus || 0,
        testDevice: hex.testDevice === false || false ? 0 : 1,
        alertDisabled: hex.alertDisabled === false || false ? 0 : 1,
        notes: hex.notes || '',
      }));

      return this.http
        .patch<any>(`${this.baseUrl}/dashboard-list-device/bulk/edit`, { devices: formattedDevices })
        .pipe(
          switchMap(() => [
            new DashboardActions.UpdateBulkDevicesSuccess(),
            // Reload the devices immediately so new rows get their real DB IDs
            new DashboardActions.LoadDevices(action.listId),
            new AppActions.HideLoader(),
          ]),
          catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
        );
    }),
  );

  // Combines counts for Active (1), Ack (2), Passive (3), and Disabled (custom logic/endpoint)
  @Effect()
  loadAlertCounts$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.LoadAlertCounts),
    switchMap(() => {
      // ForkJoin to get counts simultaneously from the /status-count/:id endpoint
      return forkJoin({
        active: this.http.get<any>(`${this.baseUrl}/dashboard-list-device/alerts/status-count/1`),
        ack: this.http.get<any>(`${this.baseUrl}/dashboard-list-device/alerts/status-count/2`),
        passive: this.http.get<any>(`${this.baseUrl}/dashboard-list-device/alerts/status-count/3`),
        disabled: this.http.get<any>(`${this.baseUrl}/dashboard-list-device/alerts/status-count/-1`), // Assuming string 'disabled' works
      }).pipe(
        switchMap((res) => {
          const payload = {
            active: res.active.data.count,
            acknowledged: res.ack.data.count,
            passive: res.passive.data.count,
            disabled: res.disabled.data.count,
          };
          return of(new DashboardActions.LoadAlertCountsSuccess(payload));
        }),
        catchError((err) => of(new DashboardActions.FetchError(err))),
      );
    }),
  );

  @Effect()
  loadAlertDevices$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.LoadAlertDevices),
    switchMap((action: DashboardActions.LoadAlertDevices) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.get<any>(`${this.baseUrl}/dashboard-list-device/alert-devices/${action.status}`).pipe(
        switchMap((res) =>
          of(new DashboardActions.LoadAlertDevicesSuccess(res.data || []), new AppActions.HideLoader()),
        ),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  changeAlertStatus$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.ChangeAlertStatus),
    switchMap((action: DashboardActions.ChangeAlertStatus) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http
        .post<any>(`${this.baseUrl}/dashboard-list-device/alerts/change-status`, {
          deviceIds: action.deviceIds,
          targetStatus: action.targetStatus,
        })
        .pipe(
          switchMap(() => [
            new DashboardActions.ChangeAlertStatusSuccess(),
            new DashboardActions.LoadAlertCounts(), // Refresh counts after changing status
            new AppActions.HideLoader(),
          ]),
          catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
        );
    }),
  );

  @Effect()
  updateList$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.UpdateList),
    switchMap((action: DashboardActions.UpdateList) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.patch<any>(`${this.baseUrl}/dashboard-list/${action.id}`, action.payload).pipe(
        switchMap((res) => [new DashboardActions.UpdateListSuccess(res.data), new AppActions.HideLoader()]),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  searchAllDevices$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.SearchAllDevices),
    switchMap((action: DashboardActions.SearchAllDevices) => {
      // Assuming your backend has a global device search endpoint, adjust URL if needed
      const params = new HttpParams()
        .set('search', action.searchTerm)
        .set('limit', '50')
        .set('searchType', 'deviceSearch');
      return this.http.get<any>(`${this.baseUrl}/device`, { params }).pipe(
        switchMap((res) => of(new DashboardActions.SearchAllDevicesSuccess(res.data))),
        catchError((err) => of(new DashboardActions.FetchError(err))),
      );
    }),
  );

  @Effect()
  deleteBulkDevices$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.DeleteBulkDevices),
    switchMap((action: DashboardActions.DeleteBulkDevices) => {
      this.store.dispatch(new AppActions.ShowLoader());

      // Sending the IDs in the body of the DELETE request
      return this.http
        .request<any>('delete', `${this.baseUrl}/dashboard-list-device/bulk/delete`, {
          body: { dashboardListId: action.listId, devices: action.payload },
        })
        .pipe(
          switchMap(() => [new DashboardActions.DeleteBulkDevicesSuccess(), new AppActions.HideLoader()]),
          catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
        );
    }),
  );

  @Effect()
  deleteList$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.DeleteList),
    switchMap((action: DashboardActions.DeleteList) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.delete<any>(`${this.baseUrl}/dashboard-list/${action.id}`).pipe(
        switchMap(() => [
          new DashboardActions.DeleteListSuccess(),
          new DashboardActions.LoadLists(),
          new AppActions.HideLoader(),
        ]),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );

  @Effect()
  generateSharedLink$ = this.actions$.pipe(
    ofType(DashboardActions.DeviceDashboardActionTypes.GenerateSharedLink),
    switchMap((action: DashboardActions.GenerateSharedLink) => {
      this.store.dispatch(new AppActions.ShowLoader());
      return this.http.post<any>(`${this.baseUrl}/auth/shared-link`, action.payload).pipe(
        switchMap((res) => [
          new DashboardActions.GenerateSharedLinkSuccess(res.data.access_token),
          new AppActions.HideLoader(),
        ]),
        catchError((err) => of(new DashboardActions.FetchError(err), new AppActions.HideLoader())),
      );
    }),
  );
}
